import React, { useState, useEffect } from 'react';
import { HIDDEN_FILE_INPUT_CLASS, JSON_ACCEPT } from '../utils/filePicker';
import BackgroundImagePicker from './BackgroundImagePicker';
import SlideMetadataPanel from './SlideMetadataPanel';
import ImageLinkInput from './ImageLinkInput';
import {
  Settings,
  Save,
  Download,
  Upload,
  Eye,
  Edit3,
  CreditCard,
  FileText,
  Palette,
  Layout,
  Type,
  Clock,
  ChevronDown,
  ChevronRight,
  Sliders,
  Monitor,
  Code,
  BookOpen,
  StickyNote,
} from 'lucide-react';

/**
 * A deck-wide on/off setting, with the sentence that says what it does.
 *
 * These used to be one-line entries in a dropdown, where a tick was the whole
 * explanation and there was no room for more. On a settings page there is
 * room, and for most of them the explanation is the point: nobody guesses what
 * "16:9 export guides" does from its name.
 */
const ToggleRow = ({ label, description, checked, onChange }) => (
  <label className="flex items-start gap-3 rounded-lg border border-gray-700 bg-gray-800 p-3 cursor-pointer hover:border-gray-600">
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(event) => onChange?.(event.target.checked)}
      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-500 bg-gray-700 text-blue-500 accent-blue-500"
    />
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-gray-200">{label}</span>
      {description && <span className="mt-0.5 block text-xs text-gray-400">{description}</span>}
    </span>
  </label>
);

/**
 * The picture on a title card, and everything that positions it.
 *
 * Written once and used by both cards. The settings page had been offering
 * four of a title card's settings out of eleven — no image at all, so the
 * `onTitleCardImageChange` prop it was being handed sat unused — while the
 * slide panel offered the lot. Two copies of this markup is how that gap
 * reopens, so there is one.
 */
const TitleCardImageFields = ({ settings, onUpdate, onImageChange, idPrefix, lightMode }) => {
  const image = settings?.image || '';
  return (
    <div className="space-y-3 rounded-lg border border-gray-700 bg-gray-800 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-200">Optional image</span>
        {image && (
          <button
            type="button"
            onClick={() => onUpdate({ image: '', imageCitation: '' })}
            className="text-xs font-semibold text-rose-400 hover:text-rose-300"
          >
            Remove image
          </button>
        )}
      </div>

      {image && (
        <img
          src={image}
          alt="Title card"
          className="max-h-32 w-full rounded-lg border border-gray-700 object-contain bg-gray-900"
        />
      )}

      <label
        htmlFor={`${idPrefix}-image-file`}
        className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-600 bg-gray-900 p-3 text-sm text-gray-300 hover:border-blue-500"
      >
        <Upload size={16} aria-hidden="true" />
        <span>{image ? 'Replace image' : 'Upload an image'}</span>
      </label>
      <input
        id={`${idPrefix}-image-file`}
        type="file"
        accept="image/*"
        className={HIDDEN_FILE_INPUT_CLASS}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onImageChange?.(file);
          event.target.value = '';
        }}
      />

      {/* An uploaded picture is embedded in the deck file as base64 and written
          out again by every save; a linked one is not. The slide panel has
          offered the choice for a while, and this page had not. */}
      <ImageLinkInput
        label="Or use a linked image"
        tone={lightMode ? 'light' : 'dark'}
        onUseLink={(url) => onUpdate({ image: url })}
      />

      {image && (
        <>
          <div>
            <label htmlFor={`${idPrefix}-image-size`} className="block text-xs font-semibold text-gray-400 mb-1.5">
              Image size: {settings?.imageSize || 300}px
            </label>
            <input
              id={`${idPrefix}-image-size`}
              type="range"
              min="50"
              max="800"
              value={settings?.imageSize || 300}
              onChange={(event) => onUpdate({ imageSize: parseInt(event.target.value, 10) })}
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor={`${idPrefix}-image-citation`} className="block text-xs font-semibold text-gray-400 mb-1.5">
              Image citation (optional)
            </label>
            <textarea
              id={`${idPrefix}-image-citation`}
              rows={2}
              value={settings?.imageCitation || ''}
              onChange={(event) => onUpdate({ imageCitation: event.target.value })}
              placeholder="Ministry of Education. (2020). Title. https://example.com"
              className="w-full p-2.5 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <span className="block text-xs font-semibold text-gray-400 mb-1.5">Text placement</span>
            <div className="flex gap-2">
              {[{ value: 'above', label: 'Above image' }, { value: 'below', label: 'Below image' }].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onUpdate({ textPlacement: option.value })}
                  aria-pressed={(settings?.textPlacement || 'below') === option.value}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                    (settings?.textPlacement || 'below') === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div>
        <span className="block text-xs font-semibold text-gray-400 mb-1.5">Title alignment</span>
        <div className="flex gap-2">
          {[{ value: 'top', label: 'Top' }, { value: 'center', label: 'Middle' }, { value: 'bottom', label: 'Bottom' }].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onUpdate({ titleAlignment: option.value })}
              aria-pressed={(settings?.titleAlignment || 'center') === option.value}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                (settings?.titleAlignment || 'center') === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={settings?.showTextOverlay === true}
          onChange={(event) => onUpdate({ showTextOverlay: event.target.checked })}
          className="mt-0.5"
        />
        <span>
          Transparent panel behind the text
          <span className="block text-xs text-gray-400">
            Keeps the title readable over a busy background image.
          </span>
        </span>
      </label>
    </div>
  );
};

// Section collapse/expand helper
const CollapsibleSection = ({ title, subtitle, icon: Icon, defaultOpen = false, badge, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        // A disclosure button that said nothing about what it disclosed: a
        // screen reader announced twelve identical "button, Slide Background"
        // controls with no way to tell an open section from a closed one.
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon size={18} className="text-gray-400" />}
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-100">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-400">
              {badge}
            </span>
          )}
          {isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};


const TemplateControlsPage = ({
  // Session details
  week,
  onWeekChange,
  date,
  onDateChange,
  instructorName,
  instructorEmail,
  onHeaderDataChange,
  headerData,
  // Layout
  bodyLayout,
  onBodyLayoutChange,
  bodyBackground,
  slideBackgrounds,
  onBodyBackgroundChange,
  onApplyBackgroundSelection,
  // Slide settings
  slideHeaderMode,
  slideHeaderColor,
  slideHeaderColors,
  onSlideHeaderModeChange,
  onSlideHeaderColorChange,
  onSlideHeaderSectionColorChange,
  slideGlobalCss,
  onSlideGlobalCssChange,
  // Title cards
  titleCardSettings,
  onTitleCardUpdate,
  onTitleCardImageChange,
  endTitleCardSettings,
  onEndTitleCardUpdate,
  onEndTitleCardImageChange,
  // Slide deck
  slideDeckName,
  onSlideDeckNameChange,
  // Export
  exportFileName,
  onExportFileNameChange,
  htmlExportTitle,
  onHtmlExportTitleChange,
  exportVersion,
  // Mode & branding
  isEditMode,
  onToggleEditMode,
  onOpenLogoSettings,
  // Save/Export actions
  onSave,
  onLoad,
  onExportPDF,
  onExportPPTX,
  onExportHTML,
  onExportOptimizedHTML,
  onExportSlimHTML,
  onExportPathHTML,
  onExportSlimJson,
  onExportStableJson,
  onExportLlmTemplate,
  onDownloadScopedCssExample,
  showAdvancedExports,
  setShowAdvancedExports,
  sections,
  // Status
  hasUnsavedChanges,
  lastSaved,
  // Helpers
  BACKGROUND_PRESETS,
  getBackgroundStyles,
  // The shared image-background presets and the "what is selected?" reader the
  // slide panel uses, so this page offers the same choices rather than its own.
  imageBackgroundPresets = [],
  getBackgroundSelectionState,
  // Slide targeting
  slides,
  /** Deck-numbered, outline-named content slides for the pickers. */
  slideOptions = [],
  // Per-slide metadata
  slideNames,
  slideSubtitles,
  slideDurations,
  slideVisibility,
  onUpdateSlideName,
  onUpdateSlideSubtitle,
  onUpdateSlideDuration,
  onSetSlideActive,
  resolveSlideName,
  // The furniture along the bottom of every slide, and the design switches
  slideNumbersEnabled,
  onSlideNumbersEnabledChange,
  deckNameBadgeEnabled,
  onDeckNameBadgeEnabledChange,
  deckNameOnContentSlides,
  onDeckNameOnContentSlidesChange,
  slideDesignMode,
  onSlideDesignModeChange,
  showAspectGuides,
  onShowAspectGuidesChange,
  lightMode = false,
}) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [backgroundTarget, setBackgroundTarget] = useState('all');
  const [pendingBackground, setPendingBackground] = useState(bodyBackground || 'white');
  const [backgroundFeedback, setBackgroundFeedback] = useState('');
  const [customColor, setCustomColor] = useState('#ffffff');
  const [customGradientFrom, setCustomGradientFrom] = useState('#ffffff');
  const [customGradientTo, setCustomGradientTo] = useState('#e0f2fe');
  const [customGradientAngle, setCustomGradientAngle] = useState(135);

  useEffect(() => {
    if (backgroundTarget === 'all') {
      setPendingBackground(bodyBackground || 'white');
      return;
    }
    const targeted = slideBackgrounds?.[backgroundTarget];
    setPendingBackground(targeted || bodyBackground || 'white');
  }, [backgroundTarget, bodyBackground, slideBackgrounds]);

  /**
   * An uploaded picture becomes the pending background.
   *
   * Takes a File rather than a change event: BackgroundImagePicker validates
   * the file type and hands the file over itself, and reading the event here
   * would mean this page kept its own, weaker, copy of that check.
   */
  const handleCustomBackgroundUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPendingBackground({ type: 'image', value: reader.result, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  /**
   * What the image picker should show as chosen.
   *
   * Read through the app's own resolver rather than by inspecting
   * `pendingBackground` here, so a preset key, an uploaded data URL and a
   * linked URL are all described the same way this deck describes them
   * everywhere else.
   */
  const pendingImageSelection = (
    getBackgroundSelectionState
      ? getBackgroundSelectionState(pendingBackground)
      : { selectedId: '', selectedLabel: '', selectedPreview: '' }
  );

  const applySelectedBackground = () => {
    const target = backgroundTarget || 'all';
    if (target === 'all') {
      onBodyBackgroundChange?.(pendingBackground);
    }
    onApplyBackgroundSelection?.({ target, background: pendingBackground });

    const targetLabel = target === 'all'
      ? 'all slides'
      : (slides?.find((slide) => slide.slideId === target)?.label || 'selected slide');
    setBackgroundFeedback(`Applied background to ${targetLabel}.`);
    window.setTimeout(() => setBackgroundFeedback(''), 2200);
  };

  const tabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'export', label: 'Export', icon: Download },
  ];

  return (
    <div className={`min-h-screen ${lightMode ? "bg-white" : "bg-gradient-to-br from-gray-900 to-gray-950"}`}>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Settings size={20} className="text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${lightMode ? "text-gray-900" : "text-white"}`}>Template Controls</h1>
              <p className={`text-sm ${lightMode ? "text-gray-600" : "text-gray-400"}`}>Configure your slide template settings, design, and export options.</p>
            </div>
          </div>
          {hasUnsavedChanges && (
            <div className="mt-3 inline-flex items-center gap-2 bg-amber-900/30 border border-amber-500/30 rounded-lg px-3 py-2">
              <Clock size={14} className="text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">Unsaved changes</span>
              <span className="text-xs text-amber-500">Last saved: {lastSaved?.toLocaleTimeString()}</span>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 bg-gray-800 rounded-xl p-1 border border-gray-700 shadow-sm overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {/* =========== SETTINGS TAB =========== */}
          {activeTab === 'settings' && (
            <>
              <CollapsibleSection title="Session Details" subtitle="Week, date, and instructor information" icon={BookOpen} defaultOpen>
                <div className="space-y-4 mt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Week</label>
                      <select
                        value={week}
                        onChange={(e) => onWeekChange(e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {Array.from({ length: 15 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>Week {i + 1}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Date</label>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => onDateChange(e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Instructor Name</label>
                      <input
                        type="text"
                        value={instructorName}
                        onChange={(e) => onHeaderDataChange('instructorName', e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Instructor Email</label>
                      <input
                        type="email"
                        value={instructorEmail}
                        onChange={(e) => onHeaderDataChange('instructorEmail', e.target.value)}
                        className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Course Title</label>
                    <input
                      type="text"
                      value={headerData?.courseTopic || ''}
                      onChange={(e) => onHeaderDataChange('courseTopic', e.target.value)}
                      placeholder="Enter course title"
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </CollapsibleSection>



              <CollapsibleSection title="Layout & Slides" subtitle="Choose presentation mode and slide deck settings" icon={Layout} defaultOpen>
                <div className="space-y-4 mt-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Body Layout</label>
                    <select
                      value={bodyLayout}
                      onChange={(e) => onBodyLayoutChange(e.target.value)}
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="accordion">Accordion</option>
                      <option value="slides">Slides</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-2">Switch between the traditional accordion view and the slide deck experience.</p>
                  </div>

                  {bodyLayout === 'slides' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">Slide Deck Name</label>
                        <input
                          type="text"
                          value={slideDeckName}
                          onChange={(e) => onSlideDeckNameChange(e.target.value)}
                          className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Capstone Production Schedule"
                        />
                        <p className="text-xs text-gray-400 mt-2">Appears on every slide and can be edited directly on the slide itself.</p>
                      </div>

                      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-semibold text-gray-300">Slide Header Color</label>
                          <span className="text-[11px] uppercase tracking-wide text-gray-400">Design</span>
                        </div>
                        <select
                          value={slideHeaderMode}
                          onChange={(e) => onSlideHeaderModeChange(e.target.value)}
                          className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="single">Use one color for all slides</option>
                          <option value="per-section">Different colors per section</option>
                        </select>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={slideHeaderColor}
                            onChange={(e) => onSlideHeaderColorChange(e.target.value)}
                            className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                          />
                          <span className="text-sm text-gray-400">Default header color</span>
                        </div>
                        {slideHeaderMode === 'per-section' && sections && (
                          <div className="grid grid-cols-1 gap-3 mt-2">
                            {sections.map((section) => (
                              <div key={section.id} className="flex items-center justify-between gap-3 bg-gray-700 p-2.5 rounded-lg border border-gray-600">
                                <span className="text-sm text-gray-300">{section.title}</span>
                                <input
                                  type="color"
                                  value={slideHeaderColors?.[section.id] || slideHeaderColor}
                                  onChange={(e) => onSlideHeaderSectionColorChange(section.id, e.target.value)}
                                  className="h-8 w-12 border border-gray-600 rounded cursor-pointer"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                </div>
              </CollapsibleSection>

              {/* Everything a slide says about itself. Only the title and subtitle
                  could be edited before, and only by clicking the slide's own
                  header; a duration could not be typed at all. */}
              <CollapsibleSection
                title="Slide Details"
                subtitle="Title, subtitle, duration and export inclusion, slide by slide"
                icon={Type}
                badge={slideOptions.length ? `${slideOptions.length} slides` : null}
              >
                <SlideMetadataPanel
                  slideOptions={slideOptions}
                  slideNames={slideNames}
                  slideSubtitles={slideSubtitles}
                  slideDurations={slideDurations}
                  slideVisibility={slideVisibility}
                  onUpdateSlideName={onUpdateSlideName}
                  onUpdateSlideSubtitle={onUpdateSlideSubtitle}
                  onUpdateSlideDuration={onUpdateSlideDuration}
                  onSetSlideActive={onSetSlideActive}
                  resolveSlideName={resolveSlideName}
                />
              </CollapsibleSection>

              {/* The deck name along the bottom of a slide, the number in the
                  corner, and the two design switches. All of it lived only in
                  the Tools dropdown, which is not where anyone looks for a
                  deck-wide setting - and the deck name badge *is* the footer,
                  so this is where "how do I change the footer?" gets answered. */}
              <CollapsibleSection
                title="Footer & Slide Numbering"
                subtitle="What appears along the bottom and in the corner of every slide"
                icon={StickyNote}
              >
                <div className="space-y-3 mt-3">
                  <ToggleRow
                    label="Deck name badge"
                    description="The deck's name in the lower-left corner. Off means it appears on no slide at all - a title card that already says what the deck is does not need it repeated."
                    checked={deckNameBadgeEnabled !== false}
                    onChange={onDeckNameBadgeEnabledChange}
                  />
                  {deckNameBadgeEnabled !== false && (
                    <div className="pl-4 border-l-2 border-gray-700">
                      <ToggleRow
                        label="Show it on content slides too"
                        description="Off keeps the badge on title and section cards only."
                        checked={deckNameOnContentSlides !== false}
                        onChange={onDeckNameOnContentSlidesChange}
                      />
                    </div>
                  )}
                  <ToggleRow
                    label="Slide numbers"
                    description="Bottom-right of every slide. Worth keeping on for a room that asks you to go back to slide 14."
                    checked={slideNumbersEnabled !== false}
                    onChange={onSlideNumbersEnabledChange}
                  />
                  <ToggleRow
                    label="16:9 export guides"
                    description="Dashed lines showing what will fit inside a PowerPoint slide. An editing aid - it is never exported."
                    checked={showAspectGuides === true}
                    onChange={onShowAspectGuidesChange}
                  />
                  <ToggleRow
                    label="Slide Designer (16:9 canvas)"
                    description="Place blocks anywhere on the slide instead of stacking them down the page."
                    checked={slideDesignMode === 'canvas'}
                    onChange={(next) => onSlideDesignModeChange?.(next ? 'canvas' : 'flow')}
                  />
                  <p className="text-xs text-gray-400">
                    These five are also in the Tools menu on the Slides tab. Same settings,
                    two places to reach them.
                  </p>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Mode & Branding" subtitle="Toggle editing mode and manage school logo" icon={Monitor}>
                <div className="space-y-3 mt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={onToggleEditMode}
                      className={`w-full p-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 ${
                        isEditMode
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md'
                          : 'bg-gray-600 hover:bg-gray-500 text-white shadow-md'
                      }`}
                    >
                      {isEditMode ? <Eye size={18} /> : <Edit3 size={18} />}
                      {isEditMode ? 'Switch to Preview' : 'Switch to Edit'}
                    </button>
                    <button
                      onClick={onOpenLogoSettings}
                      className="w-full p-3 bg-gray-600 hover:bg-gray-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
                    >
                      <Settings size={16} />
                      School Logo Settings
                    </button>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Title Cards */}
              <CollapsibleSection title="Title Card" subtitle="Configure the opening title slide" icon={CreditCard}>
                <div className="space-y-3 mt-3">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={titleCardSettings?.enabled}
                      onChange={(e) => onTitleCardUpdate({ enabled: e.target.checked })}
                    />
                    Add a title card slide at the start
                  </label>
                  <input
                    type="text"
                    value={titleCardSettings?.title || ''}
                    onChange={(e) => onTitleCardUpdate({ title: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Title card title"
                  />
                  <input
                    type="text"
                    value={titleCardSettings?.subtitle || ''}
                    onChange={(e) => onTitleCardUpdate({ subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Title card subtitle"
                  />
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={titleCardSettings?.backgroundColor || '#1e3a5f'}
                      onChange={(e) => onTitleCardUpdate({ backgroundColor: e.target.value })}
                      className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-400">Background color</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={titleCardSettings?.showCourseInfo !== false}
                      onChange={(e) => onTitleCardUpdate({ showCourseInfo: e.target.checked })}
                    />
                    Show course/instructor info
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={titleCardSettings?.showLogoOnTitle !== false}
                      onChange={(e) => onTitleCardUpdate({ showLogoOnTitle: e.target.checked })}
                    />
                    Show school logo
                  </label>
                  <TitleCardImageFields
                    settings={titleCardSettings}
                    onUpdate={onTitleCardUpdate}
                    onImageChange={onTitleCardImageChange}
                    idPrefix="title-card"
                    lightMode={lightMode}
                  />
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="End Title Card" subtitle="Configure the closing title slide" icon={CreditCard}>
                <div className="space-y-3 mt-3">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={endTitleCardSettings?.enabled}
                      onChange={(e) => onEndTitleCardUpdate({ enabled: e.target.checked })}
                    />
                    Add an end title slide
                  </label>
                  <input
                    type="text"
                    value={endTitleCardSettings?.title || ''}
                    onChange={(e) => onEndTitleCardUpdate({ title: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="End title"
                  />
                  <input
                    type="text"
                    value={endTitleCardSettings?.subtitle || ''}
                    onChange={(e) => onEndTitleCardUpdate({ subtitle: e.target.value })}
                    className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="End subtitle"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={endTitleCardSettings?.backgroundColor || '#1e3a5f'}
                        onChange={(e) => onEndTitleCardUpdate({ backgroundColor: e.target.value })}
                        className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-400">Background</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={endTitleCardSettings?.textColor || '#ffffff'}
                        onChange={(e) => onEndTitleCardUpdate({ textColor: e.target.value })}
                        className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-400">Text</span>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={endTitleCardSettings?.showCourseInfo !== false}
                      onChange={(e) => onEndTitleCardUpdate({ showCourseInfo: e.target.checked })}
                    />
                    Show course/instructor info
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={endTitleCardSettings?.showLogoOnTitle !== false}
                      onChange={(e) => onEndTitleCardUpdate({ showLogoOnTitle: e.target.checked })}
                    />
                    Show school logo
                  </label>
                  <TitleCardImageFields
                    settings={endTitleCardSettings}
                    onUpdate={onEndTitleCardUpdate}
                    onImageChange={onEndTitleCardImageChange}
                    idPrefix="end-title-card"
                    lightMode={lightMode}
                  />
                </div>
              </CollapsibleSection>
            </>
          )}

          {activeTab === 'design' && (
            <>
              <CollapsibleSection title="Slide Background" subtitle="Choose colors, gradients, patterns, or images" icon={Palette} defaultOpen>
                <div className="space-y-4 mt-3">
                  {/* Live preview of the pending background on a mini 16:9 slide */}
                  <div className="rounded-xl border border-gray-700 overflow-hidden">
                    <div
                      className="relative w-full"
                      style={{
                        aspectRatio: '16 / 9',
                        ...(getBackgroundStyles
                          ? getBackgroundStyles(typeof pendingBackground === 'string' ? BACKGROUND_PRESETS?.[pendingBackground] : pendingBackground)
                          : {})
                      }}
                    >
                      <div className="absolute top-0 left-0 right-0 h-[12%] flex items-center px-4" style={{ backgroundColor: slideHeaderColor || '#545f7d' }}>
                        <span className="text-white text-xs font-semibold">Slide preview</span>
                      </div>
                      <div className="absolute left-[6%] top-[24%] right-[40%]">
                        <div className="text-gray-800 text-sm font-bold drop-shadow-sm">Sample heading</div>
                        <div className="text-gray-600 text-xs mt-1 drop-shadow-sm">Body text preview on this background</div>
                      </div>
                      <span className="absolute bottom-2 right-3 text-[10px] px-2 py-0.5 rounded-full bg-black/40 text-white">
                        {typeof pendingBackground === 'object' ? (pendingBackground?.name || 'Custom image') : (BACKGROUND_PRESETS?.[pendingBackground]?.name || 'Preset')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="background-target" className="block text-xs font-semibold text-gray-400 mb-1.5">Apply to</label>
                    <select
                      id="background-target"
                      value={backgroundTarget}
                      onChange={(e) => setBackgroundTarget(e.target.value)}
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All slides</option>
                      {slideOptions.map((slide) => (
                        <option key={slide.slideId} value={slide.slideId}>{slide.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1.5">
                      Applying to all slides clears any background set on an individual slide.
                    </p>
                  </div>

                  {/* The shared picker, not a copy of it.
                      This section used to offer a bare file input, so the only
                      way to get a picture onto a slide from here was to embed
                      it: a background chosen here went into the deck JSON as
                      base64 and was written out again by every save, while the
                      slide panel had been able to *link* one for a while. Same
                      component now, so the two cannot drift apart again, and a
                      linked background keeps the deck small. */}
                  <BackgroundImagePicker
                    label="Background image"
                    description="Pick a preset, upload your own, or link one that is already hosted. A linked picture stays where it is hosted, so the deck file stays small."
                    presets={imageBackgroundPresets}
                    selectedId={pendingImageSelection.selectedId}
                    selectedLabel={pendingImageSelection.selectedLabel || 'No background image selected'}
                    selectedPreview={pendingImageSelection.selectedPreview}
                    // The picker hands back the whole preset; the deck stores
                    // the key, which is what keeps a preset background out of
                    // the JSON as anything more than a name.
                    onSelectPreset={(preset) => setPendingBackground(preset?.key || 'white')}
                    onUploadImage={handleCustomBackgroundUpload}
                    onUseLink={(url) => setPendingBackground({ type: 'image', value: url, name: 'Linked image' })}
                    onClear={() => setPendingBackground('white')}
                    uploadLabel="Upload background image"
                    replaceLabel="Replace background image"
                    clearLabel="Remove background image"
                    emptyLabel="No background image selected"
                    tone={lightMode ? 'light' : 'dark'}
                  />

                  {BACKGROUND_PRESETS && (
                    <>
                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Solid Colors</p>
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'color').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`h-10 rounded-lg border-2 transition-all ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-600 hover:border-gray-500'
                              }`}
                              style={getBackgroundStyles ? getBackgroundStyles(preset) : {}}
                              title={preset.name}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Custom Color Picker */}
                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Custom Color</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={customColor}
                            onChange={(e) => setCustomColor(e.target.value)}
                            className="h-10 w-14 border border-gray-600 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={customColor}
                            onChange={(e) => setCustomColor(e.target.value)}
                            className="flex-1 p-2.5 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="#ffffff"
                          />
                          <button
                            type="button"
                            onClick={() => setPendingBackground({ type: 'color', value: customColor, name: `Custom (${customColor})` })}
                            className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                              typeof pendingBackground === 'object' && pendingBackground?.type === 'color' && pendingBackground?.value === customColor
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            Use
                          </button>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Gradients</p>
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'gradient').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`h-10 rounded-lg border-2 transition-all ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-600 hover:border-gray-500'
                              }`}
                              style={getBackgroundStyles ? getBackgroundStyles(preset) : {}}
                              title={preset.name}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Custom Gradient Builder */}
                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Custom Gradient</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-400">From</label>
                              <input
                                type="color"
                                value={customGradientFrom}
                                onChange={(e) => setCustomGradientFrom(e.target.value)}
                                className="h-8 w-10 border border-gray-600 rounded cursor-pointer"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-400">To</label>
                              <input
                                type="color"
                                value={customGradientTo}
                                onChange={(e) => setCustomGradientTo(e.target.value)}
                                className="h-8 w-10 border border-gray-600 rounded cursor-pointer"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-400">Angle</label>
                              <input
                                type="number"
                                min="0"
                                max="360"
                                value={customGradientAngle}
                                onChange={(e) => setCustomGradientAngle(Number(e.target.value))}
                                className="w-16 p-1.5 border border-gray-600 bg-gray-700 text-gray-200 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const gradientValue = `linear-gradient(${customGradientAngle}deg, ${customGradientFrom} 0%, ${customGradientTo} 100%)`;
                                setPendingBackground({ type: 'gradient', value: gradientValue, name: 'Custom Gradient' });
                              }}
                              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                typeof pendingBackground === 'object' && pendingBackground?.type === 'gradient'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              Use
                            </button>
                          </div>
                          <div
                            className="h-8 rounded-lg border border-gray-600"
                            style={{ background: `linear-gradient(${customGradientAngle}deg, ${customGradientFrom} 0%, ${customGradientTo} 100%)` }}
                          />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Patterns</p>
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(BACKGROUND_PRESETS).filter(([_, p]) => p.type === 'pattern').map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setPendingBackground(key)}
                              className={`h-10 rounded-lg border-2 transition-all ${
                                pendingBackground === key
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-600 hover:border-gray-500'
                              }`}
                              style={getBackgroundStyles ? getBackgroundStyles(preset) : {}}
                              title={preset.name}
                            />
                          ))}
                        </div>
                      </div>

                    </>
                  )}

                  <div className="flex items-center justify-between gap-3 pt-2">
                    <div className="text-xs text-gray-400">
                      Selected: {typeof pendingBackground === 'object' ? (pendingBackground?.name || 'Custom image') : (BACKGROUND_PRESETS?.[pendingBackground]?.name || 'Preset')}
                    </div>
                    <button
                      type="button"
                      onClick={applySelectedBackground}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold"
                    >
                      Apply Background
                    </button>
                  </div>
                  {backgroundFeedback && <p className="text-xs text-emerald-400">{backgroundFeedback}</p>}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Global Slide CSS" subtitle="Advanced custom CSS for slide styling" icon={Code}>
                <div className="space-y-3 mt-3">
                  <textarea
                    value={slideGlobalCss}
                    onChange={(e) => onSlideGlobalCssChange(e.target.value)}
                    className="w-full h-40 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder=".slide-card { border-radius: 32px; }"
                  />
                  <p className="text-xs text-gray-400">
                    Scope styles to the slide deck (e.g. <code>.slide-deck .slide-card</code>) so they don't affect the rest of the page.
                  </p>
                  {onDownloadScopedCssExample && (
                    <button
                      type="button"
                      onClick={onDownloadScopedCssExample}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-gray-200"
                    >
                      <Download size={14} />
                      Download scoped CSS example
                    </button>
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* =========== EXPORT TAB =========== */}
          {activeTab === 'export' && (
            <>
              <CollapsibleSection title="File Naming" subtitle="Set export file names and version" icon={FileText} defaultOpen>
                <div className="space-y-4 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">Export File Name</label>
                    <input
                      type="text"
                      value={exportFileName}
                      onChange={(e) => onExportFileNameChange(e.target.value)}
                      placeholder="Week1_Lecture_2025_01_09"
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-2">HTML Export Title</label>
                    <input
                      type="text"
                      value={htmlExportTitle}
                      onChange={(e) => onHtmlExportTitleChange(e.target.value)}
                      placeholder="Lesson Title for HTML exports"
                      className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-400">Current version: <strong>v{exportVersion}</strong></span>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="JSON Exports" subtitle="Export lesson data as JSON" icon={Download} defaultOpen>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <button onClick={onSave} className="w-full p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Save size={16} />
                    Full JSON (with images)
                  </button>
                  <button onClick={onExportSlimJson} className="w-full p-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Slim JSON (no images)
                  </button>
                  {showAdvancedExports && (
                    <button onClick={onExportStableJson} className="w-full p-3 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                      <Download size={16} />
                      Stable JSON (LLM)
                    </button>
                  )}
                  <button onClick={onExportLlmTemplate} className="w-full p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    LLM Block Template
                  </button>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Presentation Exports" subtitle="Export as HTML, PDF, or PowerPoint" icon={Code}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <button onClick={onExportHTML} className="w-full p-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Full HTML
                  </button>
                  <button onClick={onExportPathHTML} className="w-full p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    HTML (Server Paths)
                  </button>
                  <button onClick={onExportSlimHTML} className="w-full p-3 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Slim HTML
                  </button>
                  <button onClick={onExportOptimizedHTML} className="w-full p-3 bg-sky-700 hover:bg-sky-800 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Optimized HTML
                  </button>
                  <button onClick={onExportPDF} className="w-full p-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    Export PDF
                  </button>
                  <button onClick={onExportPPTX} className="w-full p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <Download size={16} />
                    PowerPoint (.pptx)
                  </button>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Import" subtitle="Load previously saved content" icon={Upload}>
                <div className="space-y-3 mt-3">
                  <input
                    type="file"
                    accept={JSON_ACCEPT}
                    onChange={onLoad}
                    className={HIDDEN_FILE_INPUT_CLASS}
                    id="loadFileSettings"
                  />
                  <button
                    onClick={() => document.getElementById('loadFileSettings').click()}
                    className="w-full p-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
                  >
                    <Upload size={16} />
                    Load Content from JSON
                  </button>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Advanced" subtitle="Advanced export options" icon={Sliders}>
                <div className="space-y-3 mt-3">
                  <div className="flex items-center justify-between rounded-lg border border-gray-600 bg-gray-700 px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-200">Advanced exports</p>
                      <p className="text-xs text-gray-400">Show Stable JSON (LLM) in export menus.</p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-200">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500"
                        checked={showAdvancedExports}
                        onChange={(e) => setShowAdvancedExports(e.target.checked)}
                      />
                      Enable
                    </label>
                  </div>
                </div>
              </CollapsibleSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateControlsPage;
